import { motion } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from '../i18n';

interface HeroProps {
  onNavigateToServices: () => void;
  onNavigateToBooking: () => void;
}

export function Hero({ onNavigateToServices, onNavigateToBooking }: HeroProps) {
  const { t } = useTranslation();
  
  return (
    <section id="home" className="relative h-screen min-h-[700px] flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1761002068106-2341ebb16ca0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjaXR5JTIwc2t5bGluZSUyMHN1bnNldCUyMHByb2Zlc3Npb25hbHxlbnwxfHx8fDE3NjUwNzUzOTh8MA&ixlib=rb-4.1.0&q=80&w=1080"
          alt="City skyline"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1A33]/85 via-[#0A1A33]/75 to-[#0A1A33]/70" />
      </div>

      {/* Abstract Geometric Shapes */}
      <div className="absolute inset-0 z-0 opacity-10">
        <div className="absolute top-20 right-20 w-96 h-96 rounded-full bg-[#C6A667] blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full bg-[#C6A667] blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center mt-[80px]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-white mb-6 max-w-4xl mx-auto font-bold text-[32px] md:text-[40px]">
            {t('hero.title.main')}
          </h1>
          <p className="text-[#C6A667] mb-12 max-w-3xl mx-auto opacity-90 text-[20px]">
            {t('hero.subtitle.services')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onNavigateToBooking()}
              className="w-full sm:w-auto group bg-[#C6A667] hover:bg-[#D4B578] text-[#0A1A33] px-8 py-4 rounded-full transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
            >
              <span className="font-normal">{t('hero.cta.consultation')}</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onNavigateToServices()}
              className="w-full sm:w-auto border-2 border-white/30 hover:border-white/60 text-white px-8 py-4 rounded-full transition-all duration-300 backdrop-blur-sm hover:bg-white/5"
            >
              {t('hero.cta.viewServices')}
            </motion.button>
          </div>

          {/* Scroll Indicator */}
          <motion.div
            className="flex justify-center"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2 mt-[64px]">
              <motion.div
                className="w-1.5 h-1.5 bg-[#C6A667] rounded-full"
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
